// ---------------------------------------------------------------------------
// Homefront v3 — rocket defenses (subplot resolution)
// ---------------------------------------------------------------------------
//
// Rockets are hosted on structures — a rocket needs a live structure to sit on —
// and each one sits on a specific footprint subplot (its rocketCell). A rocket's
// protective coverage is the 19-subplot radius-2 HEXAGON centred on ITS OWN cell
// (see rocketCoverageCells) — every cell within two hex steps. A structure's
// coverage is the union of its rockets' hexagons.
//
// Interception: a winning margin M fires M shots. When a crater aims at a subplot
// inside a rocket's coverage, that rocket cancels one whole incoming shot — it
// drops the margin by 1 BEFORE the blast footprint is sized, so the footprint
// ladder is keyed on the shots that actually get through (3 rockets vs a
// margin-4 strike leave margin 1 = the 7-cell blast, not the 25-cell step). Only
// rockets whose coverage holds the aim fire, single-use. If they cancel every
// shot, the strike is "washed" — no HP is lost.

import { isStructureDestroyed, cloneHomefront } from './subplotGrid.js'
import { withinDistance } from './hexGrid.js'
import { rules, DEFAULT_RULES } from './config.js'
import { applyCraterDamage, MAX_CRATER_MARGIN } from './subplotDamage.js'

// The radius of a rocket's coverage hexagon, in hex steps.
export const ROCKET_COVERAGE_STEPS = DEFAULT_RULES.rocketCoverageSteps

// The subplots one rocket protects: every cell within ROCKET_COVERAGE_STEPS of
// its own, clipped at the field edges, crossing corridor seams freely.
export function rocketCoverageCells(cell) {
    return withinDistance(cell, rules().rocketCoverageSteps)
}

// Stations one rocket on a structure. Requires a standing (not fully destroyed)
// structure, and an undestroyed host cell when one is named. When a host subplot `subS` is given, the rocket is pinned to that
// footprint cell (at most one per cell) so it renders in place; omit it to just
// bump the count. Pure: returns { success, reason, homefront }.
export function addRocket(homefront, structureId, subS = null) {
    const st = homefront.structures[structureId]
    if (!st) return { success: false, reason: 'No such structure.', homefront }
    if (isStructureDestroyed(homefront, structureId)) {
        return { success: false, reason: 'Rockets need a standing structure.', homefront }
    }
    if (subS != null) {
        if (!st.footprint.includes(subS)) {
            return { success: false, reason: 'That subplot is not part of this structure.', homefront }
        }
        if ((st.rocketCells || []).includes(subS)) {
            return { success: false, reason: 'That subplot already holds a rocket.', homefront }
        }
        if (homefront.subplots[subS]?.destroyed) {
            return { success: false, reason: 'That subplot is destroyed.', homefront }
        }
    }
    const hf = cloneHomefront(homefront)
    const nst = hf.structures[structureId]
    nst.rockets = (nst.rockets || 0) + 1
    if (subS != null) nst.rocketCells = [...(nst.rocketCells || []), subS]
    return { success: true, reason: null, homefront: hf }
}

// The subplots protected by a structure's rockets — the union of each hosted
// rocket's coverage hexagon (empty if it hosts none).
export function rocketCoverage(homefront, structureId) {
    const st = homefront.structures[structureId]
    if (!st || (st.rockets || 0) <= 0) return new Set()
    const covered = new Set()
    for (const cell of (st.rocketCells || [])) {
        for (const s of rocketCoverageCells(cell)) covered.add(s)
    }
    return covered
}

// Live, rocket-armed structures with at least one rocket whose coverage holds
// the aim subplot, tagged with the specific host cells that can fire on it.
// A rocket on a destroyed cell never fires (applyCraterDamage removes it with
// its cell; purgeDestroyedRockets clears ones an older save still holds).
export function coveringStructures(homefront, aimS) {
    const out = []
    for (const id of Object.keys(homefront.structures)) {
        const st = homefront.structures[id]
        if ((st.rockets || 0) <= 0 || isStructureDestroyed(homefront, id)) continue
        const coveringCells = (st.rocketCells || []).filter(cell => !homefront.subplots[cell]?.destroyed && rocketCoverageCells(cell).has(aimS))
        if (coveringCells.length > 0) out.push({ structureId: id, rockets: coveringCells.length, coveringCells })
    }
    return out
}

// Removes every rocket whose host cell is destroyed — Grids resolved before
// rules v7 kept them (still firing, and drawn on their owner's side). Mutates;
// returns the cells cleared.
export function purgeDestroyedRockets(homefront) {
    const cleared = []
    for (const st of Object.values(homefront?.structures ?? {})) {
        const dead = (st.rocketCells || []).filter(c => homefront.subplots[c]?.destroyed)
        if (!dead.length) continue
        st.rocketCells = st.rocketCells.filter(c => !dead.includes(c))
        st.rockets = Math.max(0, (st.rockets || 0) - dead.length)
        cleared.push(...dead)
    }
    return cleared
}

// Fire covering rockets against a crater aimed at `aimS`. Each cancels one whole
// incoming shot, up to `maxShots` (the winning margin) or until the covering
// rockets run out. Each spent rocket drops its host cell so rocketCells stays in
// sync with the count. Pure: returns { homefront, fired, consumed }.
export function interceptCrater(homefront, aimS, maxShots) {
    const hf = cloneHomefront(homefront)
    let fired = 0
    const consumed = []
    for (const { structureId, coveringCells } of coveringStructures(hf, aimS)) {
        if (fired >= maxShots) break
        const st = hf.structures[structureId]
        const before = st.rockets
        for (const cell of coveringCells) {
            if (fired >= maxShots) break
            const idx = (st.rocketCells || []).indexOf(cell)
            if (idx !== -1) st.rocketCells.splice(idx, 1)
            st.rockets = Math.max(0, (st.rockets || 0) - 1)
            fired++
        }
        consumed.push({ structureId, before, after: st.rockets })
    }
    return { homefront: hf, fired, consumed }
}

// Full crater resolution: covering rockets each cancel a whole shot, THEN the
// blast footprint is sized from the margin that survives. The single entry
// point the war flow calls.
// Returns the applyCraterDamage report plus { rocketsFired, rocketsConsumed,
// washed, stageHomefronts }. rocketsFired (= shots cancelled) drives how many
// interceptors the animation plays.
export function resolveCrater(homefront, aimS, margin, { seed = null } = {}) {
    const { homefront: afterIntercept, fired, consumed } = interceptCrater(homefront, aimS, margin)
    const effectiveMargin = Math.max(0, margin - fired)
    const damage = applyCraterDamage(afterIntercept, aimS, effectiveMargin, { seed })
    // Staged reveal ladder — display-only: the cumulative homefront after each
    // landing shot SHORT of the last footprint-growing one (margin 1, 2, … —
    // the shapes nest, so each is a strict subset of the next). The reveal
    // swaps stage k in as the k-th landing missile strikes, making each impact
    // visibly buy its ring (7 → 13 → 19 → 25 → 31 → 37 → 43 cells); the last swap is
    // `damage.homefront` itself. The ladder caps at MAX_CRATER_MARGIN, so
    // shots past it grow nothing and get no stage. Intermediate stages carry
    // no bearing number at the aim — the intel stamp lands with the final
    // swap (a mid-strike number could name a structure a later ring then kills).
    const stageHomefronts = []
    for (let k = 1; k < Math.min(effectiveMargin, MAX_CRATER_MARGIN); k++) {
        const stage = applyCraterDamage(afterIntercept, aimS, k, { seed }).homefront
        if (stage.subplots[aimS]) {
            stage.subplots[aimS].distance = null
            stage.subplots[aimS].distanceLetter = null
        }
        stageHomefronts.push(stage)
    }
    return {
        ...damage,
        stageHomefronts,
        rocketsFired: fired,
        rocketsConsumed: consumed,
        washed: margin > 0 && effectiveMargin === 0,   // every shot cancelled — no HP lost
    }
}
